import { apiClient } from '@/shared/api';
import { API_ENDPOINTS } from '@/config';
import { buildCommonHeaders } from '@/shared/http/commonHeaders';
import type {
    AccountGetResponse,
    AccountListTxResponse,
    ApproveAccountRequest,
    GetAccountTransactionsParams,
    GetCustomerParams,
    CustomerResponse,
    UpdateLimitRequest,
    AccountListRequest
} from '@/features/card/types/account.types';
import { GenericResponse } from '@/features/card/types/card.types';
import type { ExportDirective } from '@/shared/export/types';


export const accountService = {
    /**
     * Get account details including user, profile and view permissions.
     */
    async getAccount(_uuid: string): Promise<AccountGetResponse> {

        const newHeaders = buildCommonHeaders({ withLocation: true });

        const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.account.get(_uuid), {}, { headers: newHeaders });
        return response.data.data;
    },

    async getMasterAccount(): Promise<AccountGetResponse> {

        const newHeaders = buildCommonHeaders({ withLocation: true });

        const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.account.getMasterAccount, {}, { headers: newHeaders });
        return response.data.data;
    },

    async getAccountTransactions(request: GetAccountTransactionsParams): Promise<AccountListTxResponse> {
        const newHeaders = buildCommonHeaders({
            withLocation: true
        });

        const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.account.listTx, { data: { data: request } }, { headers: newHeaders });
        return response.data.data;
    },

    /**
     * Downloads the movements report as a file (BF-613): same list command plus
     * an `export` directive, answered with the rendered PDF.
     */
    async exportMovements(request: GetAccountTransactionsParams, directive: ExportDirective) {
        const newHeaders = buildCommonHeaders({ withLocation: true });

        return apiClient.download(API_ENDPOINTS.account.listTx, {
            body: { data: { data: request, export: directive } },
            headers: newHeaders,
        });
    },

    /**
     * Get customer details including global available limit CORREGIR.
     */
    async getCustomer(params: GetCustomerParams): Promise<CustomerResponse> {

        const newHeaders = buildCommonHeaders({ withLocation: true });
        return apiClient.get<CustomerResponse>(API_ENDPOINTS.accounts.detail(params.uuid), {
            headers: newHeaders
        });
    },

    /**
     * Update account limit.
     */
    async updateLimit(request: UpdateLimitRequest): Promise<GenericResponse> {
        const newHeaders = buildCommonHeaders({ withLocation: true });
        return apiClient.post<GenericResponse>(API_ENDPOINTS.account.limitUpdate, { data: { data: request } }, {
            headers: newHeaders,
        });
    },

    /**
     * Approve a pending account.
     */
    async approveAccount(payload: ApproveAccountRequest): Promise<GenericResponse> {
        const newHeaders = buildCommonHeaders({ withLocation: true });
        return apiClient.post<GenericResponse>(API_ENDPOINTS.account.approve, { data: { data: payload } }, {
            headers: newHeaders
        });
    },

    /**
     * Reject a pending account.
     */
    async rejectAccount(payload: ApproveAccountRequest): Promise<GenericResponse> {
        const newHeaders = buildCommonHeaders({ withLocation: true });
        return apiClient.post<GenericResponse>(API_ENDPOINTS.account.approve, { data: { data: payload } }, {
            headers: newHeaders
        });
    },

    async listAccounts(params: AccountListRequest): Promise<GenericResponse> {
        const newHeaders = buildCommonHeaders({
            withLocation: true
        });

        const { ...queryParams } = params;

        const response = await apiClient.post<GenericResponse>(API_ENDPOINTS.account.list, {}, { headers: newHeaders, params: queryParams });
        return response.data;
    }
};
